import type WaveSurfer from 'wavesurfer.js';

export interface ZoomToWindowOptions {
    startSec: number;
    endSec: number;
    container: string | HTMLElement;
    paddingFrac?: number;
    minPxPerSecFloor?: number;
    maxPxPerSec?: number;
}

export interface ZoomToWindowResult {
    appliedPxPerSec: number;
    centerTime: number;
    window: [number, number];
}

/**
 * Zoom the waveform so that [startSec, endSec] fills the container.
 * Does not seek/change the playback position.
 *
 * @param ws - your WaveSurfer instance
 * @param opts - zoom options
 * @param opts.startSec - start time in seconds
 * @param opts.endSec - end time in seconds
 * @param opts.container - selector or element for measuring width
 * @param opts.paddingFrac - padding on each side of the window (default: 0.05)
 * @param opts.minPxPerSecFloor - minimum pixels per second (default: 10)
 * @param opts.maxPxPerSec - maximum pixels per second (default: 4000)
 */
export function zoomToWindow(
    ws: WaveSurfer,
    {
        startSec,
        endSec,
        container,
        paddingFrac = 0.05,
        minPxPerSecFloor = 10,
        maxPxPerSec = 4000,
    }: ZoomToWindowOptions
): ZoomToWindowResult | undefined {
    const ensureEl = (c: string | HTMLElement): HTMLElement | null =>
        typeof c === 'string' ? document.querySelector(c) : c;
    const el = ensureEl(container);
    if (!el) return;

    const doZoom = (): ZoomToWindowResult | undefined => {
        const total = ws.getDuration?.() ?? 0;
        if (!total) return;

        const a = Math.max(0, Math.min(startSec, endSec));
        const b = Math.min(total, Math.max(startSec, endSec));
        const rawWin = Math.max(0.01, b - a);

        const pad = rawWin * paddingFrac;
        const visStart = Math.max(0, a - pad);
        const visEnd = Math.min(total, b + pad);
        const visLen = Math.max(0.01, visEnd - visStart);

        const containerW = el.clientWidth || el.offsetWidth || 1;
        let pxPerSec = containerW / visLen;
        pxPerSec = Math.max(minPxPerSecFloor, Math.min(maxPxPerSec, pxPerSec));

        const center = (visStart + visEnd) / 2;

        if (typeof ws.zoom === 'function') {
            ws.zoom(pxPerSec);
        } else if (typeof ws.setOptions === 'function') {
            ws.setOptions({minPxPerSec: pxPerSec});
        }

        return {appliedPxPerSec: pxPerSec, centerTime: center, window: [a, b]};
    };

    const dur = ws.getDuration?.();
    if (!dur || !isFinite(dur) || dur === 0) {
        const once = () => {
            ws.un?.('ready', once);
            ws.un?.('decode', once);
            doZoom();
        };
        ws.on?.('ready', once);
        ws.on?.('decode', once);
        return;
    }

    return doZoom();
}
